/* ==========================================================================
   TETRALOGICX HEADER JAVASCRIPT
   --------------------------------------------------------------------------
   Includes:
   1. Mobile navigation drawer
   2. Desktop mega-menu keyboard/click handling
   3. Full-screen hero height calculation
   4. Services/Product mega-menu tabs
   5. Portfolio filter handling

   Mobile menu labels are mapped from the actual mega-menu ID/class:
   Services -> Services
   Products -> Products
   Work     -> Work
   Company  -> Company
   Insights -> Insights
   ========================================================================== */


/* ==========================================================================
   1. MOBILE NAVIGATION DRAWER
   ========================================================================== */

(function () {
  'use strict';

  var header = document.querySelector('.site-header');
  var nav = document.querySelector('.main-nav');
  var btn = document.querySelector('.mobile-menu-btn');

  if (!header || !nav || !btn) return;

  var reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var drawer;
  var panel;
  var backdrop;
  var closeBtn;
  var lastFocus;
  var built = false;


  /* ------------------------------------------------------------------------
     Utility element creator
     ------------------------------------------------------------------------ */

  function el(tag, cls, text) {
    var n = document.createElement(tag);

    if (cls) {
      n.className = cls;
    }

    if (text != null) {
      n.textContent = text;
    }

    return n;
  }


  /* ------------------------------------------------------------------------
     Portfolio tags
     ------------------------------------------------------------------------

     Service pages filter their portfolio strip by the data-portfolio-tag on the
     header link for the current page (see work-slider.js). The drawer is a
     rebuild of that same nav, so every mobile link inherits the tag of the
     mega-menu link it was built from.

     The source element is the most reliable identifier, so it is used first,
     with the destination as a fallback for links that are rendered twice in
     different columns.

     Matching by visible label was tried and removed: the Work menu's "Shopify
     Plus" link points at /pages/portfolio and is deliberately untagged, but it
     shares its label with the tagged Services link, so a label match invented a
     filter for it. Nothing else needed that fallback.

     The desktop markup is only read here, never modified.
     ------------------------------------------------------------------------ */

  var tagsByHref = null;


  function normHref(value) {
    if (!value) {
      return '';
    }

    try {
      return new URL(value, window.location.origin)
        .pathname
        .replace(/\/+$/, '')
        .toLowerCase();

    } catch (e) {
      return value.trim().toLowerCase();
    }
  }


  function indexPortfolioTags() {
    tagsByHref = {};

    Array.prototype.forEach.call(
      nav.querySelectorAll('a[data-portfolio-tag]'),
      function (src) {

        var href = normHref(src.getAttribute('href'));

        if (href && !(href in tagsByHref)) {
          tagsByHref[href] = src.getAttribute('data-portfolio-tag');
        }
      }
    );
  }


  function copyPortfolioTag(target, src) {

    if (!target || !src) {
      return;
    }

    if (!tagsByHref) {
      indexPortfolioTags();
    }

    var value = src.hasAttribute('data-portfolio-tag')
      ? src.getAttribute('data-portfolio-tag')
      : tagsByHref[normHref(src.getAttribute('href'))];

    /* An empty value is meaningful (it hides every card), so only a missing
       tag is skipped. */
    if (value == null) {
      return;
    }

    target.setAttribute('data-portfolio-tag', value);
  }


  /* ------------------------------------------------------------------------
     Get correct mobile menu label
     ------------------------------------------------------------------------

     IMPORTANT:
     We do NOT use "Services" as a generic fallback anymore.

     The desktop markup already contains:
       #megaServices
       #megaProducts
       #megaWork
       #megaCompany
       #megaInsights

     So the mobile menu gets the correct name from the actual mega menu.
     ------------------------------------------------------------------------ */

  function getMegaLabel(li, mega, index) {

    /* First try the actual desktop trigger */
    var trigger = li.querySelector(':scope > .mm-trigger');

    if (trigger) {
      var triggerText = trigger.textContent
        .replace(/[▾▼\s]+$/, '')
        .trim();

      /*
         If the desktop trigger is already correct, use it.
         This preserves custom labels if they are intentionally different.
      */
      if (
        triggerText &&
        triggerText !== 'Services'
      ) {
        return triggerText;
      }
    }


    /* Get the mega menu ID */
    var megaId = mega ? (mega.id || '').toLowerCase() : '';

    /* Get class names from the parent <li> */
    var liClass = (li.className || '').toLowerCase();


    /* Exact mapping from the real desktop mega menu */
    if (
      megaId === 'megaservices' ||
      liClass.indexOf('has-mega-services') !== -1
    ) {
      return 'Services';
    }

    if (
      megaId === 'megaproducts' ||
      liClass.indexOf('has-mega-products') !== -1
    ) {
      return 'Products';
    }

    if (
      megaId === 'megawork' ||
      liClass.indexOf('has-mega-work') !== -1
    ) {
      return 'Work';
    }

    if (
      megaId === 'megacompany' ||
      liClass.indexOf('has-mega-company') !== -1
    ) {
      return 'Company';
    }

    if (
      megaId === 'megainsights' ||
      liClass.indexOf('has-mega-insights') !== -1
    ) {
      return 'Insights';
    }


    /*
       Final fallback based on the desktop navigation order.

       Home      = 0
       Services  = 1
       Products  = 2
       Work      = 3
       Company   = 4
       Insights  = 5
       Contact   = 6
    */

    var fallbackLabels = [
      'Home',
      'Services',
      'Products',
      'Work',
      'Company',
      'Insights',
      'Contact'
    ];

    return fallbackLabels[index] || 'Menu';
  }


  /* ------------------------------------------------------------------------
     Build mobile drawer
     ------------------------------------------------------------------------ */

  function build() {

    drawer = el('div', 'mnav');

    drawer.id = 'mobileNav';

    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-label', 'Site navigation');

    drawer.hidden = true;


    /* Backdrop */
    backdrop = el('div', 'mnav-backdrop');


    /* Main panel */
    panel = el('div', 'mnav-panel');


    /* ----------------------------------------------------------------------
       Mobile header
       ---------------------------------------------------------------------- */

    var head = el('div', 'mnav-head');

    var brand = document.querySelector('.site-header .brand');

    if (brand) {

      var b = brand.cloneNode(true);

      b.removeAttribute('style');

      head.appendChild(b);
    }


    /* Close button */
    closeBtn = el('button', 'mnav-close');

    closeBtn.type = 'button';

    closeBtn.setAttribute(
      'aria-label',
      'Close navigation'
    );

    closeBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
      '<path d="M18 6 6 18M6 6l12 12"></path>' +
      '</svg>';

    head.appendChild(closeBtn);

    panel.appendChild(head);


    /* ----------------------------------------------------------------------
       Mobile menu list
       ---------------------------------------------------------------------- */

    var list = el('ul', 'mnav-list');


    Array.prototype.forEach.call(
      nav.children,
      function (li, index) {

        var mega = li.querySelector(':scope > .mega-wrap');
        var link = li.querySelector(':scope > a');


        /* ---------------------------------------------------------------
           Normal link
           Example:
             Home
             Contact
           --------------------------------------------------------------- */

        if (!mega && link) {

          var item = el('li');

          var a = el(
            'a',
            null,
            link.textContent.trim()
          );

          a.href = link.getAttribute('href');


          if (link.getAttribute('aria-current')) {
            a.setAttribute(
              'aria-current',
              'page'
            );
          }


          copyPortfolioTag(a, link);


          item.appendChild(a);

          list.appendChild(item);

          return;
        }


        /* ---------------------------------------------------------------
           Mega menu
           --------------------------------------------------------------- */

        if (mega) {

          /*
             FIX:
             Previously this was:

             var labelText = label ? ... : 'Services';

             That caused Company and Insights to become Services.

             Now the label is determined from:
             - desktop trigger
             - mega menu ID
             - mega menu class
             - navigation order
          */

          var label = li.querySelector(
            ':scope > .mm-trigger, :scope > button, :scope > span'
          );

          var labelText = getMegaLabel(
            li,
            mega,
            index
          );


          /* Group */
          var group = el(
            'li',
            'mnav-group'
          );


          /* Toggle */
          var toggle = el(
            'button',
            'mnav-toggle'
          );

          toggle.type = 'button';

          toggle.setAttribute(
            'aria-expanded',
            'false'
          );

          toggle.innerHTML =
            '<span>' +
            labelText +
            '</span>' +

            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<path d="m6 9 6 6 6-6"></path>' +
            '</svg>';


          /* Sub menu */
          var sub = el(
            'div',
            'mnav-sub'
          );

          sub.hidden = true;


          /* -------------------------------------------------------------
             Copy all mega columns into mobile menu
             ------------------------------------------------------------- */

          Array.prototype.forEach.call(
            mega.querySelectorAll('.mega-col'),
            function (col) {

              var eyebrow =
                col.querySelector('.mega-eyebrow');


              if (eyebrow) {

                sub.appendChild(
                  el(
                    'p',
                    'mnav-subhead',
                    eyebrow.textContent.trim()
                  )
                );
              }


              var ul = el('ul');


              /* ---------------------------------------------------------
                 Add link helper
                 --------------------------------------------------------- */

              function add(src, cls, text) {

                if (!src) return;

                var li2 = el('li');

                var a2 = el(
                  'a',
                  cls,
                  text
                );


                a2.href =
                  src.getAttribute('href') || '#';


                if (src.getAttribute('aria-current')) {

                  a2.setAttribute(
                    'aria-current',
                    'page'
                  );
                }


                copyPortfolioTag(a2, src);


                li2.appendChild(a2);

                ul.appendChild(li2);
              }


              /* ---------------------------------------------------------
                 Standard mega groups
                 --------------------------------------------------------- */

              var groups =
                col.querySelectorAll('.mega-group');


              if (groups.length) {

                Array.prototype.forEach.call(
                  groups,
                  function (g) {

                    var mi =
                      g.querySelector('.mega-item');


                    if (mi) {

                      var strong =
                        mi.querySelector('strong');


                      add(
                        mi,
                        'mnav-parent',
                        strong
                          ? strong.textContent.trim()
                          : mi.textContent.trim()
                      );
                    }


                    Array.prototype.forEach.call(
                      g.querySelectorAll('.mega-links a'),
                      function (sl) {

                        add(
                          sl,
                          null,
                          sl.textContent.trim()
                        );
                      }
                    );
                  }
                );


              } else {

                /* -------------------------------------------------------
                   Work / special mega menu layout
                   ------------------------------------------------------- */

                Array.prototype.forEach.call(
                  col.querySelectorAll('a'),
                  function (link) {

                    var head =
                      link.querySelector('h4') ||
                      link.querySelector('strong');


                    var parent =
                      link.classList.contains('mega-item') ||
                      link.classList.contains('mw-btn') ||
                      link.classList.contains('mw-feature');


                    add(
                      link,
                      parent
                        ? 'mnav-parent'
                        : null,
                      head
                        ? head.textContent.trim()
                        : link.textContent.trim()
                    );
                  }
                );
              }


              if (ul.children.length) {
                sub.appendChild(ul);
              }

            }
          );


          /* -------------------------------------------------------------
             Open / close mobile submenu
             ------------------------------------------------------------- */

          toggle.addEventListener(
            'click',
            function () {

              var open =
                toggle.getAttribute(
                  'aria-expanded'
                ) === 'true';


              toggle.setAttribute(
                'aria-expanded',
                String(!open)
              );


              sub.hidden = open;
            }
          );


          /* -------------------------------------------------------------
             Open current section automatically
             ------------------------------------------------------------- */

          if (
            sub.querySelector('[aria-current]') ||
            (
              label &&
              label.classList.contains('is-active')
            )
          ) {

            toggle.setAttribute(
              'aria-expanded',
              'true'
            );

            sub.hidden = false;
          }


          group.appendChild(toggle);

          group.appendChild(sub);

          list.appendChild(group);
        }
      }
    );


    panel.appendChild(list);


    /* ----------------------------------------------------------------------
       Mobile footer actions
       ---------------------------------------------------------------------- */

    var foot = el(
      'div',
      'mnav-foot'
    );


    /* Quote button */
    var quote =
      document.querySelector('.header-cta .btn');


    if (quote) {

      var q = el(
        'a',
        'btn btn-p mnav-cta',
        quote.textContent.trim()
      );


      q.href =
        quote.getAttribute('href') || '#';


      var modal =
        quote.getAttribute('data-modal-open');


      if (modal) {

        q.setAttribute(
          'data-modal-open',
          modal
        );
      }


      foot.appendChild(q);
    }


    /* Phone */
    var phone =
      document.querySelector('.ann-phone');


    if (phone) {

      var p = el(
        'a',
        'mnav-phone',
        phone.textContent.trim()
      );


      p.href =
        phone.getAttribute('href');


      foot.appendChild(p);
    }


    panel.appendChild(foot);


    /* ----------------------------------------------------------------------
       Add drawer to document
       ---------------------------------------------------------------------- */

    drawer.appendChild(backdrop);
    drawer.appendChild(panel);

    document.body.appendChild(drawer);


    /* ----------------------------------------------------------------------
       Close handlers
       ---------------------------------------------------------------------- */

    backdrop.addEventListener(
      'click',
      close
    );


    closeBtn.addEventListener(
      'click',
      close
    );


    panel.addEventListener(
      'click',
      function (e) {

        var a =
          e.target.closest('a');


        if (a) {
          close();
        }
      }
    );


    built = true;
  }


  /* ==========================================================================
     MOBILE FOCUS TRAP
     ========================================================================== */

  var SEL =
    'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';


  function trap(e) {

    if (e.key !== 'Tab') {
      return;
    }


    var f =
      Array.prototype.filter.call(
        panel.querySelectorAll(SEL),
        function (n) {
          return n.offsetParent !== null;
        }
      );


    if (!f.length) {
      return;
    }


    var first = f[0];
    var last = f[f.length - 1];


    if (
      e.shiftKey &&
      document.activeElement === first
    ) {

      e.preventDefault();

      last.focus();


    } else if (
      !e.shiftKey &&
      document.activeElement === last
    ) {

      e.preventDefault();

      first.focus();
    }
  }


  /* ==========================================================================
     MOBILE KEYBOARD
     ========================================================================== */

  function onKey(e) {

    if (e.key === 'Escape') {

      close();

      return;
    }


    trap(e);
  }


  /* ==========================================================================
     OPEN MOBILE MENU
     ========================================================================== */

  function open() {

    if (!built) {
      build();
    }


    lastFocus =
      document.activeElement;


    drawer.hidden = false;


    /* Force transition frame */
    void drawer.offsetWidth;


    drawer.classList.add(
      'is-open'
    );


    document.documentElement.classList.add(
      'mnav-locked'
    );


    btn.setAttribute(
      'aria-expanded',
      'true'
    );


    document.addEventListener(
      'keydown',
      onKey
    );


    closeBtn.focus();
  }


  /* ==========================================================================
     CLOSE MOBILE MENU
     ========================================================================== */

  function close() {

    if (
      !built ||
      drawer.hidden
    ) {
      return;
    }


    drawer.classList.remove(
      'is-open'
    );


    document.documentElement.classList.remove(
      'mnav-locked'
    );


    btn.setAttribute(
      'aria-expanded',
      'false'
    );


    document.removeEventListener(
      'keydown',
      onKey
    );


    var done = function () {

      drawer.hidden = true;
    };


    if (reduce) {

      done();

    } else {

      setTimeout(
        done,
        260
      );
    }


    if (
      lastFocus &&
      lastFocus.focus
    ) {

      lastFocus.focus();
    }
  }


  /* ==========================================================================
     MOBILE MENU BUTTON
     ========================================================================== */

  btn.setAttribute(
    'aria-expanded',
    'false'
  );


  btn.setAttribute(
    'aria-controls',
    'mobileNav'
  );


  btn.addEventListener(
    'click',
    function (e) {

      e.preventDefault();


      if (
        built &&
        !drawer.hidden
      ) {

        close();

      } else {

        open();
      }
    }
  );


  /* ==========================================================================
     CLOSE MOBILE MENU WHEN DESKTOP NAV RETURNS
     ========================================================================== */

  window.addEventListener(
    'resize',
    function () {

      if (
        window.innerWidth > 900 &&
        built &&
        !drawer.hidden
      ) {

        close();
      }
    }
  );

})();


/* ==========================================================================
   2. DESKTOP MEGA MENU
   ========================================================================== */

(function () {
  'use strict';

  var items =
    document.querySelectorAll(
      '.main-nav .has-mega'
    );


  if (!items.length) {
    return;
  }


  var openItem = null;


  /* ------------------------------------------------------------------------
     Close desktop mega menu
     ------------------------------------------------------------------------ */

  function close(li, refocus) {

    if (
      !li ||
      !li.classList.contains('is-open')
    ) {
      return;
    }


    li.classList.remove(
      'is-open'
    );


    var trigger =
      li.querySelector('.mm-trigger');


    if (trigger) {

      trigger.setAttribute(
        'aria-expanded',
        'false'
      );
    }


    if (openItem === li) {

      openItem = null;
    }


    if (!openItem) {

      document.removeEventListener(
        'keydown',
        onKey,
        true
      );


      document.removeEventListener(
        'click',
        onDocClick,
        true
      );
    }


    if (
      refocus &&
      trigger
    ) {

      trigger.focus();
    }
  }


  /* ------------------------------------------------------------------------
     Open desktop mega menu
     ------------------------------------------------------------------------ */

  function open(li) {

    if (
      openItem &&
      openItem !== li
    ) {

      close(
        openItem,
        false
      );
    }


    li.classList.add(
      'is-open'
    );


    var trigger =
      li.querySelector('.mm-trigger');


    if (trigger) {

      trigger.setAttribute(
        'aria-expanded',
        'true'
      );
    }


    openItem = li;


    document.addEventListener(
      'keydown',
      onKey,
      true
    );


    document.addEventListener(
      'click',
      onDocClick,
      true
    );
  }


  /* ------------------------------------------------------------------------
     Escape
     ------------------------------------------------------------------------ */

  function onKey(e) {

    if (e.key === 'Escape') {

      close(
        openItem,
        true
      );
    }
  }


  /* ------------------------------------------------------------------------
     Outside click
     ------------------------------------------------------------------------ */

  function onDocClick(e) {

    if (
      openItem &&
      !openItem.contains(e.target)
    ) {

      close(
        openItem,
        false
      );
    }
  }


  /* ------------------------------------------------------------------------
     Wire all desktop mega menus
     ------------------------------------------------------------------------ */

  Array.prototype.forEach.call(
    items,
    function (li) {

      var trigger =
        li.querySelector('.mm-trigger');


      var panel =
        li.querySelector('.mega-wrap');


      if (
        !trigger ||
        !panel
      ) {
        return;
      }


      /* Click trigger */
      trigger.addEventListener(
        'click',
        function (e) {

          e.preventDefault();


          if (
            li.classList.contains('is-open')
          ) {

            close(
              li,
              false
            );

          } else {

            open(li);
          }
        }
      );


      /* Close when focus leaves item */
      li.addEventListener(
        'focusout',
        function (e) {

          if (
            li.classList.contains('is-open') &&
            !li.contains(e.relatedTarget)
          ) {

            close(
              li,
              false
            );
          }
        }
      );


      /* Close after clicking any link */
      panel.addEventListener(
        'click',
        function (e) {

          if (
            e.target.closest('a')
          ) {

            close(
              li,
              false
            );
          }
        }
      );

    }
  );

})();


/* ==========================================================================
   3. FULL-SCREEN HERO HEIGHT
   ========================================================================== */

(function () {
  'use strict';

  var hero =
    document.querySelector('.hero-fit');


  if (!hero) {
    return;
  }


  var raf;


  function fit() {

    var top =
      hero.getBoundingClientRect().top -
      document.body.getBoundingClientRect().top;


    document.documentElement.style.setProperty(
      '--hero-chrome',
      Math.round(top) + 'px'
    );
  }


  function schedule() {

    if (raf) {
      cancelAnimationFrame(raf);
    }


    raf =
      requestAnimationFrame(fit);
  }


  /* Initial */
  fit();


  /* Resize */
  window.addEventListener(
    'resize',
    schedule
  );


  /* Page load */
  window.addEventListener(
    'load',
    schedule
  );


  /* ------------------------------------------------------------------------
     Observe announcement/header height changes
     ------------------------------------------------------------------------ */

  if (window.ResizeObserver) {

    var ro =
      new ResizeObserver(
        schedule
      );


    [
      '.announce-bar',
      '.site-header'
    ].forEach(
      function (sel) {

        var element =
          document.querySelector(sel);


        if (element) {
          ro.observe(element);
        }
      }
    );
  }


  /* Wait for fonts */
  if (
    document.fonts &&
    document.fonts.ready
  ) {

    document.fonts.ready.then(
      schedule
    );
  }

})();


/* ==========================================================================
   4. SERVICES / PRODUCT MEGA MENU TABS
   ========================================================================== */

(function () {
  'use strict';

  var rails =
    document.querySelectorAll('.mt-rail');


  if (!rails.length) {
    return;
  }


  Array.prototype.forEach.call(
    rails,
    wireRail
  );


  function wireRail(rail) {

    var tabs =
      Array.prototype.slice.call(
        rail.querySelectorAll('.mt-tab')
      );


    if (!tabs.length) {
      return;
    }


    /* ----------------------------------------------------------------------
       Get corresponding panel
       ---------------------------------------------------------------------- */

    function panelFor(tab) {

      return document.getElementById(
        tab.getAttribute(
          'aria-controls'
        )
      );
    }


    /* ----------------------------------------------------------------------
       Select tab
       ---------------------------------------------------------------------- */

    function select(tab, focus) {

      tabs.forEach(
        function (t) {

          var on =
            t === tab;


          t.classList.toggle(
            'is-active',
            on
          );


          t.setAttribute(
            'aria-selected',
            String(on)
          );


          t.tabIndex =
            on ? 0 : -1;


          var p =
            panelFor(t);


          if (p) {

            p.classList.toggle(
              'is-active',
              on
            );


            p.hidden =
              !on;
          }
        }
      );


      if (focus) {
        tab.focus();
      }
    }


    /* ----------------------------------------------------------------------
       Wire each tab
       ---------------------------------------------------------------------- */

    tabs.forEach(
      function (tab, i) {

        /* Click */
        tab.addEventListener(
          'click',
          function () {

            select(
              tab,
              false
            );
          }
        );


        /* Hover */
        tab.addEventListener(
          'mouseenter',
          function () {

            select(
              tab,
              false
            );
          }
        );


        /* Keyboard */
        tab.addEventListener(
          'keydown',
          function (e) {

            var k =
              e.key;

            var next = null;


            if (
              k === 'ArrowDown' ||
              k === 'ArrowRight'
            ) {

              next =
                tabs[
                  (i + 1) %
                  tabs.length
                ];


            } else if (
              k === 'ArrowUp' ||
              k === 'ArrowLeft'
            ) {

              next =
                tabs[
                  (i - 1 + tabs.length) %
                  tabs.length
                ];


            } else if (
              k === 'Home'
            ) {

              next =
                tabs[0];


            } else if (
              k === 'End'
            ) {

              next =
                tabs[
                  tabs.length - 1
                ];
            }


            if (next) {

              e.preventDefault();


              select(
                next,
                true
              );
            }
          }
        );
      }
    );


    /* ----------------------------------------------------------------------
       Reset to first tab after hover menu closes
       ---------------------------------------------------------------------- */

    var li =
      rail.closest('.has-mega');


    if (li) {

      li.addEventListener(
        'mouseleave',
        function () {

          if (
            !li.classList.contains('is-open')
          ) {

            select(
              tabs[0],
              false
            );
          }
        }
      );
    }

  }

})();


/* ==========================================================================
   5. MEGA MENU LINKS -> PORTFOLIO FILTER TABS
   ========================================================================== */

document.addEventListener(
  'DOMContentLoaded',
  function () {

    var megaLinks =
      document.querySelectorAll(
        '.mega-links a'
      );


    var filterTabs =
      document.querySelectorAll(
        '#filterTabs [data-filter]'
      );


    if (
      !megaLinks.length ||
      !filterTabs.length
    ) {
      return;
    }


    megaLinks.forEach(
      function (link) {

        link.addEventListener(
          'click',
          function () {

            var url =
              new URL(
                link.href,
                window.location.origin
              );


            var filterValue =
              url.hash
                ? url.hash.substring(1)
                : 'all';


            var matchingFilter =
              document.querySelector(
                '#filterTabs [data-filter="' +
                filterValue +
                '"]'
              );


            if (!matchingFilter) {
              return;
            }


            /* Remove active state */
            filterTabs.forEach(
              function (button) {

                button.classList.remove(
                  'active'
                );


                button.setAttribute(
                  'aria-pressed',
                  'false'
                );
              }
            );


            /* Activate matching filter */
            matchingFilter.classList.add(
              'active'
            );


            matchingFilter.setAttribute(
              'aria-pressed',
              'true'
            );


            /* Trigger actual filter */
            matchingFilter.click();

          }
        );
      }
    );

  }
);